import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
  signal,
  TemplateRef,
} from '@angular/core';
import { NgOptimizedImage, NgTemplateOutlet } from '@angular/common';
import { LanguageService } from '@core/services';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { map, tap } from 'rxjs';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslocoDirective } from '@jsverse/transloco';
import { MatFormField, MatOption, MatSelect, MatSelectTrigger } from '@angular/material/select';
import { hasFlag } from 'country-flag-icons';
import { languageToCountryMap } from '@core/const';
import { environment } from '@environments/environment';

@Component({
  selector: 'sky-page-header',
  imports: [
    NgTemplateOutlet,
    NgOptimizedImage,
    MatSelect,
    ReactiveFormsModule,
    MatOption,
    MatSelectTrigger,
    TranslocoDirective,
    MatFormField,
  ],
  templateUrl: './page-header.component.html',
  styles: `
    sky-select ::ng-deep > div {
      padding: 0 !important;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PageHeaderComponent implements OnInit {
  private readonly languageService = inject(LanguageService);
  private readonly destroyRef = inject(DestroyRef);

  language = new FormControl<string>(this.languageService.getSelectedLanguage(), [Validators.required]);

  moreActions = input<TemplateRef<unknown>>();
  languages = toSignal(
    this.languageService.getLanguages().pipe(
      map(languages =>
        Array.from(languages).map(([key, value]) => {
          return {
            label: value,
            value: key,
            flag: this.getFlagByValue(value),
          };
        }),
      ),
      takeUntilDestroyed(this.destroyRef),
    ),
  );
  version = signal<string>(environment.version);

  get langValue() {
    return this.language.value;
  }

  ngOnInit() {
    this.language.valueChanges
      .pipe(
        tap(lang => {
          this.languageService.setLanguage(lang);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  getFlagByValue(value: string) {
    const countryCode = languageToCountryMap[value?.toUpperCase()] || value?.toUpperCase();
    return hasFlag(countryCode) ? `/assets/flags/${countryCode}.svg` : undefined;
  }
}
